import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { analytics } from '../lib/analytics';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DetailPanel from '../components/DetailPanel';
import { addItemToFolder, removeItemFromFolder, fetchItemFolders } from '../lib/supabase';
import { FREE_SKILL_CREATE_LIMIT, FREE_PLAYBOOK_CREATE_LIMIT } from '../context/AuthContext';
import { uploadFile } from '../lib/supabase';
import { parseVariables, assemblePrompt, varLabel, validateVarValues, SKILL_CATEGORIES } from '../lib/skills';
import { publishListing } from '../lib/supabase';
import RunInAIButton from '../components/RunInAIButton';
import { OUTPUT_TYPES, MODELS } from '../constants';


// ── Square crop helper for cover image uploads ───────────────────────────────
async function cropSquare(file, maxSize = 1080) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const size = Math.min(img.width, img.height, maxSize);
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, (img.width-size)/2, (img.height-size)/2, size, size, 0, 0, size, size);
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Blob failed')), 'image/jpeg', 0.92);
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}

// Template system user — content owned by this account is protected from admin deletion


// Template system user — content owned by this account is protected from admin deletion
const TEMPLATE_USER_ID = '85ad93de-9f42-476c-8a0c-20ac5afa2008';

const VAR_COLORS = [
  { bg: 'rgba(127,119,221,0.15)', border: 'rgba(127,119,221,0.4)', text: '#afa9ec' },
  { bg: 'rgba(29,158,117,0.15)',  border: 'rgba(29,158,117,0.4)',  text: '#5dcaa5' },
  { bg: 'rgba(239,159,39,0.15)',  border: 'rgba(239,159,39,0.4)',  text: '#ef9f27' },
  { bg: 'rgba(133,183,235,0.15)', border: 'rgba(133,183,235,0.4)', text: '#85b7eb' },
  { bg: 'rgba(226,75,74,0.15)',   border: 'rgba(226,75,74,0.4)',   text: '#f09595' },
  { bg: 'rgba(175,169,236,0.15)', border: 'rgba(175,169,236,0.4)', text: '#7f77dd' },
];
const varColor = idx => VAR_COLORS[idx % VAR_COLORS.length];

function VarPill({ name, idx }) {
  const c = varColor(idx);
  return (
    <span style={{
      fontSize: 10, padding: '2px 7px', borderRadius: 4,
      background: c.bg, border: `0.5px solid ${c.border}`, color: c.text,
      fontFamily: 'var(--mono)',
    }}>{`{{${name}}}`}</span>
  );
}

// eslint-disable-next-line no-unused-vars
function HighlightedPrompt({ template }) {
  if (!template) return null;
  const vars = parseVariables(template);
  const colorMap = {};
  vars.forEach((v, i) => { colorMap[v] = varColor(i); });
  const parts = template.split(/(\{\{[^}]+\}\})/g);
  return (
    <span style={{ fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {parts.map((part, i) => {
        const match = part.match(/^\{\{([^}]+)\}\}$/);
        if (match) {
          const c = colorMap[match[1].trim()] || VAR_COLORS[0];
          return <span key={i} style={{ background: c.bg, border: `0.5px solid ${c.border}`, color: c.text, borderRadius: 4, padding: '1px 5px', fontSize: 11 }}>{part}</span>;
        }
        return <span key={i} style={{ color: 'var(--muted)' }}>{part}</span>;
      })}
    </span>
  );
}

// ── Use skill modal ───────────────────────────────────────────
function UseSkillModal({ skill, onClose }) {
  // Lazy-load full skill data (prompt + variables) if not already present
  const [fullSkill, setFullSkill] = useState(skill);
  const [loadingSkill, setLoadingSkill] = useState(!skill.prompt);
  useEffect(() => {
    if (skill.prompt !== undefined) { setFullSkill(skill); return; }
    setLoadingSkill(true);
    import('../lib/supabase').then(({ fetchSkillById }) =>
      fetchSkillById(skill.id)
        .then(full => { if (full) setFullSkill(full); })
        .catch(console.error)
        .finally(() => setLoadingSkill(false))
    );
  }, [skill.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const vars = fullSkill.variables || [];
  const [values, setValues] = useState(() => Object.fromEntries(vars.map(v => [v.name, ''])));
  // Re-initialise values when skill loads
  useEffect(() => {
    setValues(Object.fromEntries((fullSkill.variables || []).map(v => [v.name, ''])));
  }, [fullSkill.id, fullSkill.prompt]); // eslint-disable-line react-hooks/exhaustive-deps

  const [copied, setCopied] = useState(false);
  const assembled = assemblePrompt(fullSkill.prompt || '', values);
  const hasUnfilled = assembled.includes('{{');
  const allFilled = validateVarValues(vars, values);
  const canCopy = !loadingSkill && (vars.length === 0 || (allFilled && !hasUnfilled));
  const catInfo = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];

  const copy = () => {
    navigator.clipboard?.writeText(assembled).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, maxWidth: 560, maxHeight: '92vh', overflow: 'auto' }}>
        <div style={s.modalHeader}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>{catInfo.icon}</span>
              <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>{skill.title}</span>
            </div>
            {skill.description && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{skill.description}</div>}
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loadingSkill && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[80, 60, 70].map((w, i) => (
                <div key={i} className="skeleton" style={{ height: 14, borderRadius: 6, width: `${w}%`, background: 'var(--border2)' }} />
              ))}
              <div style={{ fontSize: 12, color: 'var(--dim)', textAlign: 'center', paddingTop: 4 }}>Loading skill…</div>
            </div>
          )}
          {!loadingSkill && vars.length > 0 && (
            <div>
              <div style={s.sectionLabel}>Fill in variables</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {vars.map((v, i) => {
                  const c = varColor(i);
                  return (
                    <div key={v.name}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: c.bg, border: `0.5px solid ${c.border}`, color: c.text, fontFamily: 'var(--mono)' }}>
                          {`{{${v.name}}}`}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {v.label || varLabel(v.name)}
                          {v.required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
                        </span>
                      </label>
                      {v.multiline ? (
                        <textarea style={{ ...s.varInput, minHeight: 70 }}
                          placeholder={v.placeholder || `Enter ${varLabel(v.name).toLowerCase()}...`}
                          value={values[v.name]}
                          onChange={e => setValues(p => ({ ...p, [v.name]: e.target.value }))} />
                      ) : (
                        <input style={s.varInput}
                          placeholder={v.placeholder || `Enter ${varLabel(v.name).toLowerCase()}...`}
                          value={values[v.name]}
                          onChange={e => setValues(p => ({ ...p, [v.name]: e.target.value }))} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div style={{ ...s.sectionLabel, display: 'flex', justifyContent: 'space-between' }}>
              <span>Assembled prompt</span>
              {hasUnfilled && <span style={{ color: 'var(--dim)', fontSize: 10, fontWeight: 400 }}>Fill all variables</span>}
            </div>
            <div style={{
              background: 'var(--bg)', border: '0.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '12px',
              maxHeight: 200, overflowY: 'auto',
            }}>
              <HighlightedPrompt template={assembled} />
            </div>
          </div>

          {/* Reference file */}
          {skill.reference_url && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--surface2)', border: '0.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px',
            }}>
              <span style={{ fontSize: 18 }}>📎</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {skill.reference_name || 'Reference file'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>Attached by skill creator</div>
              </div>
              <a href={skill.reference_url} target="_blank" rel="noreferrer" style={{
                fontSize: 12, color: 'var(--accent)', textDecoration: 'none',
                border: '0.5px solid var(--accent-border)', borderRadius: 4, padding: '4px 10px',
              }}>Download</a>
            </div>
          )}

          {skill.model && (
            <div style={{ fontSize: 12, color: 'var(--dim)' }}>
              Recommended: <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{skill.model}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={copy} disabled={!canCopy} style={{
              flex: 1,
              background: copied ? 'rgba(29,158,117,0.15)' : 'var(--accent)',
              color: copied ? '#5dcaa5' : '#fff',
              border: copied ? '0.5px solid rgba(29,158,117,0.4)' : 'none',
              borderRadius: 'var(--radius-sm)', padding: '11px',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
              cursor: !canCopy ? 'not-allowed' : 'pointer', opacity: !canCopy ? 0.55 : 1,
              transition: 'all 0.2s',
            }}>
              {copied ? '✓ Copied to clipboard!' : 'Copy prompt'}
            </button>
            {canCopy && (
              <RunInAIButton
                prompt={assembled}
                model={skill.model}
                entryType={skill.type}
                size="sm"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Skill form ────────────────────────────────────────────────
// ── Searchable model picker ───────────────────────────────────
function ModelPicker({ value, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const ref                 = useRef(null);

  // Flatten all models with their group
  const allModels = useMemo(() => {
    const flat = [];
    Object.entries(MODELS).forEach(([group, models]) => {
      models.forEach(m => flat.push({ model: m, group }));
    });
    return flat;
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return allModels;
    const q = search.toLowerCase();
    return allModels.filter(({ model, group }) =>
      model.toLowerCase().includes(q) || group.toLowerCase().includes(q)
    );
  }, [allModels, search]);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayValue = value || 'Any model';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...s.input, textAlign: 'left', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--bg)',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? 'var(--text)' : 'var(--dim)' }}>
          {displayValue}
        </span>
        <span style={{ fontSize: 10, color: 'var(--dim)', flexShrink: 0, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 8, zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}>
          {/* Search input */}
          <div style={{ padding: '8px 10px', borderBottom: '0.5px solid var(--border)' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search models..."
              style={{
                width: '100%', background: 'var(--bg)',
                border: '0.5px solid var(--border2)', borderRadius: 6,
                padding: '6px 10px', color: 'var(--text)',
                fontFamily: 'var(--font)', fontSize: 12, outline: 'none',
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {/* Any model option */}
            <div
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{
                padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                color: !value ? 'var(--accent)' : 'var(--muted)',
                background: !value ? 'var(--accent-dim)' : 'transparent',
                borderBottom: '0.5px solid var(--border)',
              }}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
            >
              Any model
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: '12px', fontSize: 12, color: 'var(--dim)', textAlign: 'center' }}>
                No models match "{search}"
              </div>
            ) : (
              (() => {
                // Group results
                const groups = {};
                filtered.forEach(({ model, group }) => {
                  if (!groups[group]) groups[group] = [];
                  groups[group].push(model);
                });
                return Object.entries(groups).map(([group, models]) => (
                  <div key={group}>
                    <div style={{
                      padding: '5px 12px 3px',
                      fontSize: 10, color: 'var(--dim)',
                      textTransform: 'uppercase', letterSpacing: '0.07em',
                      background: 'var(--surface2)',
                    }}>{group}</div>
                    {models.map(m => (
                      <div
                        key={m}
                        onClick={() => { onChange(m); setOpen(false); setSearch(''); }}
                        style={{
                          padding: '7px 12px', fontSize: 12.5, cursor: 'pointer',
                          color: value === m ? 'var(--accent)' : 'var(--text)',
                          background: value === m ? 'var(--accent-dim)' : 'transparent',
                          borderBottom: '0.5px solid var(--border)',
                        }}
                        onMouseEnter={e => { if (value !== m) e.currentTarget.style.background = 'var(--surface2)'; }}
                        onMouseLeave={e => { if (value !== m) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {m}
                      </div>
                    ))}
                  </div>
                ));
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SkillForm({ skill, onSave, onClose }) {
  const { user, isPro, isAdmin } = useAuth();
  const canMakePrivate = isPro || isAdmin;
  const isEdit = !!skill;
  const fileRef = useRef();
  const promptRef = useRef();

  // If editing, lazy-load the full skill (prompt may be missing from list fetch)
  const [, setFullSkillData] = useState(skill);
  const [loadingFull, setLoadingFull] = useState(isEdit && !skill?.prompt);
  useEffect(() => {
    if (!isEdit || skill?.prompt !== undefined) { setFullSkillData(skill); return; }
    setLoadingFull(true);
    import('../lib/supabase').then(({ fetchSkillById }) =>
      fetchSkillById(skill.id)
        .then(full => { if (full) { setFullSkillData(full); setForm(f => ({ ...f, prompt: full.prompt || '', output_example: full.output_example || '' })); } })
        .catch(console.error)
        .finally(() => setLoadingFull(false))
    );
  }, [skill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({
    title:          skill?.title          || '',
    description:    skill?.description    || '',
    prompt:         skill?.prompt         || '',
    output_example:  skill?.output_example   || '',
    output_image_url: skill?.output_image_url || '',
    prompt_image_url: skill?.prompt_image_url || '',
    model:          skill?.model          || '',
    type:           skill?.type           || 'text',
    category:       skill?.category       || 'general',
    tags:           (skill?.tags || []).join(', '),
    is_public:      skill?.is_public      ?? false,
    slug:           skill?.slug           || '',
  });
  const [saving, setSaving]         = useState(false);
  const [errors, setErrors]         = useState({});
  const [refFile, setRefFile]       = useState(null);
  const [refPreview, setRefPreview] = useState(skill?.reference_name || null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newVarName, setNewVarName]  = useState('');

  const detectedVars = useMemo(() => parseVariables(form.prompt), [form.prompt]);
  // Show loading state in prompt textarea while full skill loads
  const promptPlaceholder = loadingFull ? 'Loading skill data…' : 'Write your prompt here. Use {{variable_name}} for inputs.';
  const [varMeta, setVarMeta] = useState(() => {
    const map = {};
    (skill?.variables || []).forEach(v => { map[v.name] = v; });
    return map;
  });

  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateVarMeta = (name, field, value) =>
    setVarMeta(prev => ({ ...prev, [name]: { ...prev[name], name, [field]: value } }));

  // Insert {{variable}} at cursor position in the textarea
  const insertVariable = (name) => {
    const el = promptRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const text  = form.prompt;
    const inserted = `{{${name}}}`;
    const next = text.slice(0, start) + inserted + text.slice(end);
    setV('prompt', next);
    // Restore cursor after insert
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + inserted.length, start + inserted.length);
    }, 0);
  };

  const handleQuickAddVar = () => {
    const name = newVarName.trim().replace(/\s+/g, '_').toLowerCase();
    if (!name) return;
    insertVariable(name);
    setNewVarName('');
  };

  const handleRefFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    setRefFile(f);
    setRefPreview(f.name);
  };

  // Auto-generate slug from title if not manually set
  const autoSlug = form.title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 48);

  const handleSave = async () => {
    const e = {};
    if (!form.title.trim())       e.title       = true;
    if (!form.description.trim()) e.description = true;
    if (!form.prompt.trim())      e.prompt      = true;
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      let refUrl  = skill?.reference_url  || null;
      let refName = skill?.reference_name || null;

      if (refFile) {
        setUploadingRef(true);
        const uploaded = await uploadFile(refFile, user.id);
        refUrl  = uploaded.url;
        refName = refFile.name;
        setUploadingRef(false);
      }

      const variables = detectedVars.map(name => ({
        name,
        label:       varMeta[name]?.label       || varLabel(name),
        placeholder: varMeta[name]?.placeholder || '',
        required:    varMeta[name]?.required    ?? true,
        multiline:   varMeta[name]?.multiline   ?? false,
      }));

      const payload = {
        title:          form.title.trim(),
        description:    form.description.trim() || null,
        prompt:         form.prompt.trim(),
        output_example:   form.output_example.trim() || null,
        output_image_url: form.output_image_url || null,
        prompt_image_url: form.prompt_image_url || null,
        model:          form.model || null,
        type:           form.type,
        category:       form.category,
        tags:           form.tags.split(',').map(t => t.trim()).filter(Boolean),
        is_public:      form.is_public,
        variables,
        reference_url:  refUrl,
        reference_name: refName,
        slug:           form.slug.trim() || autoSlug || null,
        user_id:        user.id,
      };

      let saved;
      if (isEdit) {
        saved = await (await import('../lib/supabase')).updateSkill(skill.id, payload, user.id);
      } else {
        saved = await (await import('../lib/supabase')).insertSkill(payload);
      }
      onSave(saved);
    } catch (err) { alert('Error: ' + err.message); }
    setSaving(false);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, maxWidth: 640, maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={s.modalHeader}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>
              {isEdit ? 'Edit skill' : 'New skill'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 2 }}>
              Use {'{{variable}}'} in your prompt to create dynamic inputs
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Row 1: Title + Category ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'start' }}>
            <div style={s.field}>
              <label style={s.label}>Skill name *</label>
              <input
                style={{ ...s.input, borderColor: errors.title ? 'rgba(226,75,74,0.6)' : undefined }}
                placeholder="e.g. Blog post outline generator"
                value={form.title}
                onChange={e => setV('title', e.target.value)}
                autoFocus
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Category</label>
              <select style={{ ...s.input, width: 'auto' }} value={form.category} onChange={e => setV('category', e.target.value)}>
                {SKILL_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Description ── */}
          <div style={s.field}>
            <label style={s.label}>
              Description <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>
              {errors.description && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--danger)', fontWeight: 400 }}>Required — help others understand what this skill does</span>}
            </label>
            <input style={{ ...s.input, borderColor: errors.description ? 'rgba(226,75,74,0.6)' : undefined }}
              placeholder="Generates a structured outline for any blog topic with SEO-friendly H2s"
              value={form.description} onChange={e => { setV('description', e.target.value); if (errors.description) setErrors(p => ({ ...p, description: false })); }} />
          </div>

          {/* ── Prompt template — main area ── */}
          <div style={s.field}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
              <label style={s.label}>Prompt template *</label>
              {/* Quick variable insert */}
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <input
                  value={newVarName}
                  onChange={e => setNewVarName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleQuickAddVar()}
                  placeholder="var name"
                  style={{
                    background: 'var(--bg)', border: '0.5px solid var(--border2)',
                    borderRadius: 5, padding: '3px 8px', fontSize: 11,
                    color: 'var(--muted)', fontFamily: 'var(--mono)', width: 80, outline: 'none',
                  }}
                />
                <button
                  onClick={handleQuickAddVar}
                  title="Insert variable at cursor"
                  style={{
                    background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
                    borderRadius: 5, padding: '3px 8px', fontSize: 11,
                    color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font)',
                  }}>
                  + Insert {'{{}}'}
                </button>
              </div>
            </div>

            <textarea
              ref={promptRef}
              style={{
                ...s.input, resize: 'vertical', minHeight: 140, lineHeight: 1.7,
                fontFamily: 'var(--mono)', fontSize: 12,
                borderColor: errors.prompt ? 'rgba(226,75,74,0.6)' : undefined,
              }}
              placeholder={promptPlaceholder}
              disabled={loadingFull}
              value={form.prompt}
              onChange={e => setV('prompt', e.target.value)}
            />

            {/* Live variable detection */}
            {detectedVars.length > 0 && (
              <div style={{
                marginTop: 8, background: 'var(--surface2)',
                border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                  {detectedVars.length} variable{detectedVars.length !== 1 ? 's' : ''} detected — click to configure
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {detectedVars.map((name, i) => {
                    const c = varColor(i);
                    const meta = varMeta[name] || {};
                    return (
                      <div key={name} style={{
                        display: 'grid', gridTemplateColumns: 'auto 1fr 1fr auto auto',
                        gap: 8, alignItems: 'center',
                        background: 'var(--bg)', borderRadius: 6, padding: '8px 10px',
                        border: `0.5px solid ${c.border}`,
                      }}>
                        {/* Pill */}
                        <VarPill name={name} idx={i} />
                        {/* Label */}
                        <input
                          style={{ ...s.input, fontSize: 11, padding: '5px 8px' }}
                          placeholder={`Label (${varLabel(name)})`}
                          value={meta.label || ''}
                          onChange={e => updateVarMeta(name, 'label', e.target.value)}
                        />
                        {/* Placeholder hint */}
                        <input
                          style={{ ...s.input, fontSize: 11, padding: '5px 8px' }}
                          placeholder="Hint text..."
                          value={meta.placeholder || ''}
                          onChange={e => updateVarMeta(name, 'placeholder', e.target.value)}
                        />
                        {/* Required toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={meta.required ?? true} onChange={e => updateVarMeta(name, 'required', e.target.checked)} />
                          Req
                        </label>
                        {/* Multiline toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={meta.multiline ?? false} onChange={e => updateVarMeta(name, 'multiline', e.target.checked)} />
                          Long
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Output example ── */}
          <div style={s.field}>
            <label style={s.label}>
              Example output
              <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>— paste a real result to show others what to expect</span>
            </label>
            <textarea
              style={{ ...s.input, resize: 'vertical', minHeight: 80, lineHeight: 1.6, fontSize: 12, fontFamily: 'var(--mono)' }}
              placeholder="Paste an example of what this skill produces when run..."
              value={form.output_example}
              onChange={e => setV('output_example', e.target.value)}
            />
          </div>

          {/* ── Output image ── */}
          <div style={s.field}>
            <label style={s.label}>
              Thumbnail / Cover image
              <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>— shown on skill card and marketplace listing</span>
            </label>
            <SkillImageUpload
              value={form.output_image_url}
              onChange={v => setV('output_image_url', v)}
              userId={user?.id}
              placeholder="Upload a square image (1:1 ratio) — auto-cropped to 1080×1080"
            />
          </div>

          {/* ── Prompt screenshot ── */}
          <div style={s.field}>
            <label style={s.label}>
              Prompt in use
              <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>— optional screenshot showing the prompt being used</span>
            </label>
            <SkillImageUpload
              value={form.prompt_image_url}
              onChange={v => setV('prompt_image_url', v)}
              userId={user?.id}
              placeholder="Upload a screenshot of the prompt in action"
            />
          </div>

          {/* ── Model + Output type ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={s.field}>
              <label style={s.label}>Recommended model</label>
              <ModelPicker value={form.model} onChange={v => setV('model', v)} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Output type</label>
              <select style={s.input} value={form.type} onChange={e => setV('type', e.target.value)}>
                {OUTPUT_TYPES.map(t => (
                  <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Visibility ── */}
          <div style={s.field}>
            <label style={s.label}>Visibility</label>
            <div style={{ display: 'flex', gap: 7 }}>
              {[{ v: false, label: '🔒 Private — only you' }, { v: true, label: '🌐 Public — share with world' }].map(({ v, label }) => (
                <div key={String(v)} onClick={() => {
                  if (v === false && !canMakePrivate) {
                    alert('Private skills require a Pro plan. Upgrade to keep your skills private.');
                    return;
                  }
                  setV('is_public', v);
                }} style={{
                  flex: 1, textAlign: 'center', padding: '10px',
                  border: `0.5px solid ${form.is_public === v ? 'var(--accent-border)' : 'var(--border2)'}`,
                  background: form.is_public === v ? 'var(--accent-dim)' : 'var(--surface2)',
                  borderRadius: 'var(--radius-sm)', fontSize: 12.5,
                  color: form.is_public === v ? 'var(--accent)' : 'var(--muted)', cursor: 'pointer',
                  transition: 'all 0.12s',
                }}>{label}</div>
              ))}
            </div>
          </div>

          {/* ── Advanced (progressive disclosure) ── */}
          <div>
            <button
              onClick={() => setShowAdvanced(a => !a)}
              style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontSize: 12, color: 'var(--dim)', display: 'flex', alignItems: 'center', gap: 5,
                fontFamily: 'var(--font)',
              }}>
              <span style={{ fontSize: 10 }}>{showAdvanced ? '▼' : '▶'}</span>
              Advanced options
            </button>

            {showAdvanced && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>

                {/* Tags */}
                <div style={s.field}>
                  <label style={s.label}>Tags</label>
                  <input style={s.input} placeholder="writing, marketing, seo (comma separated)"
                    value={form.tags} onChange={e => setV('tags', e.target.value)} />
                </div>

                {/* Slug — CLI handle */}
                <div style={s.field}>
                  <label style={s.label}>
                    CLI slug
                    <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>— used when running from Claude Code or CLI</span>
                  </label>
                  <input
                    style={{ ...s.input, fontFamily: 'var(--mono)', fontSize: 12 }}
                    placeholder={autoSlug || 'my-skill-name'}
                    value={form.slug}
                    onChange={e => setV('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  />
                  {autoSlug && !form.slug && (
                    <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 2 }}>
                      Auto: <code style={{ fontFamily: 'var(--mono)', color: 'var(--muted)' }}>{autoSlug}</code>
                    </div>
                  )}
                </div>

                {/* Reference file */}
                <div style={s.field}>
                  <label style={s.label}>Reference file <span style={{ color: 'var(--dim)', fontWeight: 400 }}>(PDF or image — optional)</span></label>
                  <div onClick={() => fileRef.current.click()} style={{
                    border: '0.5px dashed var(--border2)', borderRadius: 'var(--radius-sm)',
                    padding: '12px', cursor: 'pointer', textAlign: 'center',
                    fontSize: 12.5, color: 'var(--dim)', transition: 'border-color 0.12s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
                    {refPreview
                      ? <span style={{ color: 'var(--muted)' }}>📎 {refPreview}</span>
                      : <span>Click to attach a reference PDF or image</span>}
                  </div>
                  <input ref={fileRef} type="file" accept=".pdf,image/*"
                    style={{ display: 'none' }} onChange={handleRefFile} />
                  {refPreview && (
                    <button onClick={() => { setRefFile(null); setRefPreview(null); }}
                      style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 11, cursor: 'pointer', textAlign: 'left', padding: 0, marginTop: 3 }}>
                      × Remove file
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
            <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving || uploadingRef || !form.description.trim()} style={s.saveBtn}>
              {uploadingRef ? 'Uploading...' : saving ? 'Saving...' : isEdit ? 'Update skill' : 'Create skill'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Skill card ────────────────────────────────────────────────

// ── FolderPicker — reusable dropdown to assign an item to folders ──────────
function FolderPicker({ itemType, itemId, userId, folders, onChanged }) {
  const [open,       setOpen]       = useState(false);
  const [assigned,   setAssigned]   = useState(new Set());
  const [loading,    setLoading]    = useState(false);
  const ref = useRef(null);

  // Load current folder memberships when opening
  const handleOpen = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setLoading(true);
    setOpen(true);
    try {
      const ids = await fetchItemFolders(itemType, itemId, userId);
      setAssigned(new Set(ids));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = async (e, folderId) => {
    e.stopPropagation();
    const next = new Set(assigned);
    try {
      if (next.has(folderId)) {
        await removeItemFromFolder(folderId, itemType, itemId);
        next.delete(folderId);
      } else {
        await addItemToFolder(folderId, itemType, itemId, userId);
        next.add(folderId);
      }
      setAssigned(next);
      onChanged?.();
    } catch (err) { console.error(err); }
  };

  if (!folders || folders.length === 0) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={handleOpen}
        title="Add to folder"
        style={{
          ...s.iconActionBtn,
          color: assigned.size > 0 ? 'var(--accent)' : 'var(--dim)',
          borderColor: assigned.size > 0 ? 'var(--accent-border)' : 'var(--border2)',
          background: assigned.size > 0 ? 'var(--accent-dim)' : 'none',
          position: 'relative',
        }}>
        📁
        {assigned.size > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: 'var(--accent)', color: '#fff',
            fontSize: 8, fontWeight: 700, minWidth: 14, height: 14,
            borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid var(--surface)',
          }}>{assigned.size}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 10, minWidth: 180, maxHeight: 240,
          overflowY: 'auto', zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '8px 12px 4px', fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '0.5px solid var(--border)' }}>
            Add to folder
          </div>
          {loading ? (
            <div style={{ padding: '12px', fontSize: 12, color: 'var(--dim)', textAlign: 'center' }}>Loading…</div>
          ) : (
            folders.map(f => (
              <div key={f.id}
                onClick={e => toggle(e, f.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', cursor: 'pointer',
                  background: assigned.has(f.id) ? 'var(--accent-dim)' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (!assigned.has(f.id)) e.currentTarget.style.background = 'var(--surface2)'; }}
                onMouseLeave={e => { if (!assigned.has(f.id)) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: f.color, flexShrink: 0,
                  boxShadow: assigned.has(f.id) ? `0 0 6px ${f.color}` : 'none',
                }} />
                <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: assigned.has(f.id) ? 'var(--accent)' : 'var(--text)' }}>
                  {f.name}
                </span>
                {assigned.has(f.id) && <span style={{ fontSize: 10, color: 'var(--accent)' }}>✓</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SkillCard({ skill, onUse, onView, onEdit, onDelete, onFork, onPublish, isOwner, currentUserId, folders, userId, isListed }) {
  const [forking, setForking] = useState(false);
  const catInfo  = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];
  const typeInfo = OUTPUT_TYPES.find(t => t.id === skill.type) || { icon: '📄', label: skill.type };
  const canPublish = isOwner && onPublish && skill.is_public;

  // Colour-coded visibility
  const isPublic  = skill.is_public;
  const isInstalled = !!skill.forked_from;
  const borderColor = isPublic
    ? 'rgba(29,158,117,0.35)'   // green — public
    : 'var(--border)';           // neutral — private

  const handleFork = async (e) => {
    e.stopPropagation();
    setForking(true);
    try { await onFork(skill); }
    catch (err) { alert(err.message); }
    setForking(false);
  };

  return (
    <div
      onClick={() => onView && onView(skill)}
      style={{
        background: 'var(--surface)',
        border: `0.5px solid ${borderColor}`,
        borderRadius: 'var(--radius)', padding: '0',
        display: 'flex', flexDirection: 'column',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        cursor: 'pointer', overflow: 'hidden',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = isPublic ? 'rgba(29,158,117,0.6)' : 'var(--border2)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Thin visibility colour strip — always present */}
      <div style={{ height: 3, background: isPublic ? '#1d9e75' : 'var(--border2)', transition: 'background 0.2s' }} />

      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {/* Icon slot — thumbnail if uploaded, category icon otherwise */}
          <div style={{
            width: 44, height: 44, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
            background: isPublic ? 'rgba(29,158,117,0.12)' : 'var(--accent-dim)',
            border: `0.5px solid ${isPublic ? 'rgba(29,158,117,0.3)' : 'var(--accent-border)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            {skill.output_image_url
              ? <img src={skill.output_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : catInfo.icon
            }
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {skill.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
              {/* Visibility pill — prominent, colour-coded */}
              {isPublic
                ? <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: 'rgba(29,158,117,0.12)', border: '0.5px solid rgba(29,158,117,0.3)', color: '#1d9e75' }}>Public</span>
                : <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: 'var(--surface2)', border: '0.5px solid var(--border2)', color: 'var(--dim)' }}>Private</span>
              }
              {isListed && (
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: 'rgba(127,119,221,0.12)', border: '0.5px solid rgba(127,119,221,0.3)', color: '#7f77dd' }}>
                  ✦ Listed
                </span>
              )}
              {isInstalled && (
                <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: 'rgba(29,158,117,0.08)', border: '0.5px solid rgba(29,158,117,0.2)', color: '#1d9e75' }}>⬇ Installed</span>
              )}
              <span style={{ fontSize: 10, color: 'var(--dim)' }}>{catInfo.label}</span>
              {skill.profiles?.username && <span style={{ fontSize: 10, color: 'var(--dim)' }}>· @{skill.profiles.username}</span>}
            </div>
          </div>

          {/* Owner icon controls — edit, folder, delete only. Publish moved to action row. */}
          {isOwner && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button onClick={e => { e.stopPropagation(); onEdit(skill); }} style={s.iconActionBtn} title="Edit">✎</button>
              {userId && <FolderPicker itemType="skill" itemId={skill.id} userId={userId} folders={folders} />}
              <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this skill?')) onDelete(skill.id); }}
                style={{ ...s.iconActionBtn, color: 'var(--danger)' }} title="Delete">✕</button>
            </div>
          )}
        </div>

        {skill.description && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>{skill.description}</div>
        )}

        {/* Variable pills */}
        {(skill.variables || []).length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {(skill.variables || []).map((v, i) => <VarPill key={v.name} name={v.name} idx={i} />)}
          </div>
        )}

        {/* Meta tags */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span className={`tag tag-${skill.type}`} style={{ fontSize: 10 }}>{typeInfo.icon} {typeInfo.label}</span>
          {skill.model && <span className="model-badge" style={{ fontSize: 10 }}>{skill.model}</span>}
          {(skill.tags || []).slice(0, 2).map(t => (
            <span key={t} className="tag tag-custom" style={{ fontSize: 10 }}>{t}</span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--dim)' }}>
            {skill.use_count > 0 && `${skill.use_count} uses`}
            {skill.fork_count > 0 && ` · ${skill.fork_count} forks`}
          </span>
        </div>

        {/* Output example */}
        {skill.output_example && (
          <div style={{
            fontSize: 11, color: 'var(--muted)', lineHeight: 1.5,
            background: 'var(--surface2)', borderRadius: 6, padding: '8px 10px',
            fontFamily: 'var(--mono)', maxHeight: 60, overflow: 'hidden',
            borderLeft: '2px solid var(--accent-border)',
          }}>
            <div style={{ fontSize: 9, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3, fontFamily: 'var(--font)' }}>Example output</div>
            {skill.output_example.slice(0, 120)}{skill.output_example.length > 120 ? '…' : ''}
          </div>
        )}

        {/* Action row — Use + Publish (owner) or Fork (others) */}
        <div style={{ display: 'flex', gap: 7, marginTop: 2 }}>
          <button onClick={e => { e.stopPropagation(); onUse(skill); }} style={s.useBtn}>
            ▶ Use
          </button>

          {canPublish && (
            isListed
              ? <button onClick={e => { e.stopPropagation(); onPublish(skill); }}
                  style={{
                    ...s.useBtn, flex: 'none', padding: '7px 14px',
                    background: 'rgba(127,119,221,0.12)',
                    color: '#7f77dd',
                    border: '0.5px solid rgba(127,119,221,0.35)',
                    fontSize: 12, fontWeight: 500,
                  }}>
                  ✦ On Marketplace
                </button>
              : <button onClick={e => { e.stopPropagation(); onPublish(skill); }}
                  style={{
                    ...s.useBtn, flex: 'none', padding: '7px 14px',
                    background: 'rgba(29,158,117,0.1)',
                    color: '#1d9e75',
                    border: '0.5px solid rgba(29,158,117,0.3)',
                    fontSize: 12, fontWeight: 500,
                  }}>
                  Publish →
                </button>
          )}

          {/* Fork — non-owners of public skills */}
          {isPublic && !isOwner && (
            <button onClick={handleFork} disabled={forking} style={{
              ...s.useBtn, flex: 'none', background: 'var(--surface2)',
              color: 'var(--muted)', border: '0.5px solid var(--border2)',
              fontSize: 12, padding: '7px 12px',
            }}>
              {forking ? '...' : '🍴 Fork'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// -- Playbook color presets ────────────────────────────────────────
const PLAYBOOK_COLORS = [
  '#7f77dd','#1d9e75','#e24b4a','#ef9f27',
  '#85b7eb','#5dcaa5','#f09595','#e879a0',
];

// -- Playbook Form (create/edit) ───────────────────────────────────
function PlaybookForm({ playbook, mySkills, onSave, onClose }) {
  const { user } = useAuth();
  const isEdit = !!playbook;
  const [form, setForm] = useState({
    title:       playbook?.title       || '',
    description: playbook?.description || '',
    category:    playbook?.category    || 'general',
    cover_color:     playbook?.cover_color     || PLAYBOOK_COLORS[0],
    cover_image_url: playbook?.cover_image_url || '',
    slug:        playbook?.slug        || '',
    is_public:   playbook?.is_public   ?? false,
  });
  const [selectedSkills, setSelectedSkills] = useState(
    () => (playbook?.skills || []).map(s => s.id)
  );
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const autoSlug = form.title.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 48);

  const filteredSkills = useMemo(() => {
    if (!search.trim()) return mySkills;
    const q = search.toLowerCase();
    return mySkills.filter(s =>
      s.title?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q)
    );
  }, [mySkills, search]);

  const toggleSkill = (id) => {
    setSelectedSkills(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Please add a title.'); return; }
    if (!form.description.trim()) { alert('Please add a description — help users understand what this playbook does.'); return; }
    setSaving(true);
    try {
      const { createPlaybook, updatePlaybook, addSkillToPlaybook, removeSkillFromPlaybook, reorderPlaybookSkills } = await import('../lib/supabase');
      const payload = {
        title:       form.title.trim(),
        description: form.description.trim() || null,
        category:    form.category,
        cover_color:     form.cover_color,
        cover_image_url: form.cover_image_url || null,
        slug:            form.slug.trim() || autoSlug || null,
        is_public:   form.is_public,
      };

      let saved;
      if (isEdit) {
        saved = await updatePlaybook(playbook.id, payload, user.id);
        // Sync skills
        const existing = (playbook?.skills || []).map(s => s.id);
        const toAdd    = selectedSkills.filter(id => !existing.includes(id));
        const toRemove = existing.filter(id => !selectedSkills.includes(id));
        await Promise.all([
          ...toAdd.map((id, i) => addSkillToPlaybook(playbook.id, id, existing.length + i)),
          ...toRemove.map(id => removeSkillFromPlaybook(playbook.id, id)),
        ]);
        if (selectedSkills.length) await reorderPlaybookSkills(playbook.id, selectedSkills);
      } else {
        saved = await createPlaybook(payload);
        for (let i = 0; i < selectedSkills.length; i++) {
          await addSkillToPlaybook(saved.id, selectedSkills[i], i);
        }
      }
      onSave({ ...saved, skills: mySkills.filter(s => selectedSkills.includes(s.id)) });
    } catch (err) { alert('Error: ' + err.message); }
    setSaving(false);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, maxWidth: 660, maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
        <div style={s.modalHeader}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>
              {isEdit ? 'Edit pack' : 'New playbook'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 2 }}>
              Bundle related skills into a shareable playbook
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Title */}
          <div style={s.field}>
            <label style={s.label}>Playbook name *</label>
            <input style={s.input} placeholder="e.g. Content Marketing Playbook"
              value={form.title} onChange={e => setV('title', e.target.value)} autoFocus />
          </div>

          {/* Description */}
          <div style={s.field}>
            <label style={s.label}>Description <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span></label>
            <textarea style={{ ...s.input, minHeight: 72, resize: 'vertical', lineHeight: 1.6 }}
              placeholder="What workflow does this playbook cover? Who is it for? What will they be able to do?"
              value={form.description} onChange={e => setV('description', e.target.value)} />
          </div>

          {/* Thumbnail */}
          <div style={s.field}>
            <label style={s.label}>
              Thumbnail / Cover image
              <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>— shown on playbook card and marketplace listing</span>
            </label>
            <SkillImageUpload
              value={form.cover_image_url || ''}
              onChange={v => setV('cover_image_url', v)}
              userId={user?.id}
              placeholder="Upload a square image (1:1 ratio) — auto-cropped to 1080×1080"
              folder="playbook-covers"
            />
          </div>

          {/* Category + Color */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'start' }}>
            <div style={s.field}>
              <label style={s.label}>Category</label>
              <select style={s.input} value={form.category} onChange={e => setV('category', e.target.value)}>
                {SKILL_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Color</label>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', paddingTop: 4 }}>
                {PLAYBOOK_COLORS.map(c => (
                  <button key={c} onClick={() => setV('cover_color', c)} style={{
                    width: 24, height: 24, borderRadius: 6, background: c, border: 'none',
                    outline: form.cover_color === c ? '2.5px solid var(--text)' : 'none',
                    outlineOffset: 2, cursor: 'pointer',
                    transform: form.cover_color === c ? 'scale(1.15)' : 'scale(1)',
                    transition: 'transform 0.1s',
                  }} />
                ))}
              </div>
            </div>
          </div>

          {/* Skills picker */}
          <div style={s.field}>
            <label style={s.label}>
              Skills in this playbook
              <span style={{ color: 'var(--dim)', fontWeight: 400, marginLeft: 6 }}>
                {selectedSkills.length} selected
              </span>
            </label>
            <input style={{ ...s.input, marginBottom: 8 }} placeholder="Search your skills..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {mySkills.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--dim)', padding: '12px 0', textAlign: 'center' }}>
                No skills yet — create some skills first then add them to a playbook.
              </div>
            ) : (
              <div style={{
                border: '0.5px solid var(--border2)', borderRadius: 8,
                maxHeight: 260, overflowY: 'auto',
              }}>
                {filteredSkills.map(skill => {
                  const catInfo = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];
                  const selected = selectedSkills.includes(skill.id);
                  return (
                    <div key={skill.id}
                      onClick={() => toggleSkill(skill.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px', cursor: 'pointer',
                        borderBottom: '0.5px solid var(--border)',
                        background: selected ? 'var(--accent-dim)' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--surface2)'; }}
                      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        border: `0.5px solid ${selected ? 'var(--accent)' : 'var(--border2)'}`,
                        background: selected ? 'var(--accent)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: '#fff',
                      }}>{selected ? '✓' : ''}</div>
                      <span style={{ fontSize: 13 }}>{catInfo.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {skill.title}
                        </div>
                        {skill.description && (
                          <div style={{ fontSize: 11, color: 'var(--dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {skill.description}
                          </div>
                        )}
                      </div>
                      {(skill.variables || []).length > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--dim)', flexShrink: 0 }}>
                          {skill.variables.length} var{skill.variables.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div style={s.field}>
            <label style={s.label}>Visibility</label>
            <div style={{ display: 'flex', gap: 7 }}>
              {[{ v: false, label: '🔒 Private — only you' }, { v: true, label: '🌐 Public — share with world' }].map(({ v, label }) => (
                <div key={String(v)} onClick={() => setV('is_public', v)} style={{
                  flex: 1, textAlign: 'center', padding: '10px',
                  border: `0.5px solid ${form.is_public === v ? 'var(--accent-border)' : 'var(--border2)'}`,
                  background: form.is_public === v ? 'var(--accent-dim)' : 'var(--surface2)',
                  borderRadius: 'var(--radius-sm)', fontSize: 12.5,
                  color: form.is_public === v ? 'var(--accent)' : 'var(--muted)', cursor: 'pointer',
                  transition: 'all 0.12s',
                }}>{label}</div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
            <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.description.trim()} style={{
              ...s.saveBtn,
              opacity: !form.title.trim() ? 0.5 : 1,
            }}>
              {saving ? 'Saving...' : isEdit ? 'Update playbook' : 'Create playbook'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Skill Detail Modal ────────────────────────────────────────
function SkillDetailModal({ skill, onClose, onUse, onEdit, onFork, isOwner }) {
  const [forking, setForking] = useState(false);
  // Lazy-load full skill (prompt, output_example) if not in list data
  const [fullSkill, setFullSkill] = useState(skill);
  const [loadingFull, setLoadingFull] = useState(!skill.prompt);
  useEffect(() => {
    if (skill.prompt !== undefined) { setFullSkill(skill); return; }
    setLoadingFull(true);
    import('../lib/supabase').then(({ fetchSkillById }) =>
      fetchSkillById(skill.id)
        .then(full => { if (full) setFullSkill(full); })
        .catch(console.error)
        .finally(() => setLoadingFull(false))
    );
  }, [skill.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const catInfo = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];
  const typeInfo = OUTPUT_TYPES.find(t => t.id === skill.type) || { icon: '📄', label: skill.type };
  // color available for future use
  const handleFork = async () => {
    setForking(true);
    try { await onFork(skill); onClose(); }
    catch (err) { alert(err.message); }
    setForking(false);
  };

  return (
    <div style={{ ...s.overlay, zIndex: 10100 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, maxWidth: 580, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ ...s.modalHeader, gap: 12, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 12, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}>{catInfo.icon}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{skill.title}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, color: 'var(--accent)' }}>{catInfo.label}</span>
                <span className={`tag tag-${skill.type}`} style={{ fontSize: 10 }}>{typeInfo.icon} {typeInfo.label}</span>
                {skill.model && <span className="model-badge" style={{ fontSize: 10 }}>{skill.model}</span>}
                {skill.is_public
                  ? <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 4, background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)', color: '#1d9e75' }}>🌐 public</span>
                  : <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 4, background: 'rgba(95,94,90,0.1)', border: '0.5px solid rgba(95,94,90,0.25)', color: 'var(--dim)' }}>🔒 private</span>
                }
              </div>
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {skill.description && (
            <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.65, margin: 0 }}>{skill.description}</p>
          )}

          {/* Variables */}
          {(skill.variables || []).length > 0 && (
            <div>
              <div style={s.sectionLabel}>Variables ({skill.variables.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {skill.variables.map((v, i) => {
                  const c = varColor(i);
                  return (
                    <div key={v.name} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      background: 'var(--surface2)', borderRadius: 8, padding: '9px 12px',
                    }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: c.bg, border: `0.5px solid ${c.border}`, color: c.text, fontFamily: 'var(--mono)', flexShrink: 0, marginTop: 1 }}>
                        {`{{${v.name}}}`}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>
                          {v.label || v.name}
                          {v.required && <span style={{ color: 'var(--danger)', marginLeft: 3, fontSize: 11 }}>required</span>}
                        </div>
                        {v.placeholder && (
                          <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 2 }}>e.g. {v.placeholder}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Prompt template */}
          {loadingFull && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[90, 75, 82].map((w, i) => (
                <div key={i} className="skeleton" style={{ height: 13, borderRadius: 4, width: `${w}%`, background: 'var(--border2)' }} />
              ))}
            </div>
          )}
          {fullSkill.prompt && (
            <div>
              <div style={s.sectionLabel}>Prompt template</div>
              <div style={{
                background: 'var(--bg)', border: '0.5px solid var(--border)',
                borderRadius: 'var(--radius-sm)', padding: '12px',
                maxHeight: 180, overflowY: 'auto',
              }}>
                <HighlightedPrompt template={fullSkill.prompt} />
              </div>
            </div>
          )}

          {/* Output example */}
          {fullSkill.output_example && (
            <div>
              <div style={s.sectionLabel}>Example output</div>
              <div style={{
                fontSize: 12, color: 'var(--muted)', lineHeight: 1.65,
                background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px',
                fontFamily: 'var(--mono)', borderLeft: '2px solid var(--accent-border)',
                maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {fullSkill.output_example}
              </div>
            </div>
          )}

          {/* Tags */}
          {(skill.tags || []).filter(t => !t.startsWith('category:')).length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {skill.tags.filter(t => !t.startsWith('category:')).map(t => (
                <span key={t} className="tag tag-custom" style={{ fontSize: 10 }}>{t}</span>
              ))}
            </div>
          )}

          {/* CLI slug */}
          {skill.slug && (
            <div style={{ fontSize: 11, color: 'var(--dim)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: 'var(--accent)', fontSize: 9 }}>⌘</span>
              <code style={{ fontFamily: 'var(--mono)' }}>cpg install {skill.slug}</code>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: '14px 20px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8 }}>
          {isOwner && (
            <button onClick={() => { onClose(); onEdit(skill); }} style={{ ...s.cancelBtn, flex: 'none' }}>✎ Edit</button>
          )}
          {!isOwner && skill.is_public && (
            <button onClick={handleFork} disabled={forking} style={{ ...s.cancelBtn, flex: 'none' }}>
              {forking ? '...' : '🍴 Fork'}
            </button>
          )}
          <button onClick={() => { onClose(); onUse(skill); }} style={{ ...s.saveBtn, flex: 1 }}>
            ▶ Use skill
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Playbook Detail Modal ─────────────────────────────────────
function PlaybookDetailModal({ playbook, onClose, onInstall, onEdit, onDelete, onSkillClick, isOwner, installing }) {
  const catInfo = SKILL_CATEGORIES.find(c => c.id === playbook.category) || SKILL_CATEGORIES[0];
  const color = playbook.cover_color || '#7f77dd';
  const skills = playbook.skills || [];

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...s.modal, maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        {/* Colour banner */}
        <div style={{ height: 6, background: color, flexShrink: 0, boxShadow: `0 2px 12px ${color}40` }} />

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '0.5px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, flexShrink: 0,
              background: color + '20', border: `0.5px solid ${color}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
            }}>{catInfo.icon}</div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{playbook.title}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{catInfo.label}</span>
                <span style={{ fontSize: 11, color: 'var(--dim)' }}>·</span>
                <span style={{ fontSize: 11, color: 'var(--dim)' }}>{skills.length} skill{skills.length !== 1 ? 's' : ''}</span>
                {playbook.install_count > 0 && (
                  <><span style={{ fontSize: 11, color: 'var(--dim)' }}>·</span>
                  <span style={{ fontSize: 11, color: 'var(--dim)' }}>{playbook.install_count} installs</span></>
                )}
                {!playbook.is_public && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: 'rgba(95,94,90,0.12)', color: 'var(--dim)' }}>private</span>
                )}
                {playbook.forked_from && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)', color: '#1d9e75' }}>⬇ installed</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {isOwner && (
                <>
                  <button onClick={() => { onClose(); onEdit(playbook); }} style={s.iconActionBtn}>✎</button>
                  <button onClick={() => { if (window.confirm('Delete this playbook?')) { onDelete(playbook.id); onClose(); } }}
                    style={{ ...s.iconActionBtn, color: 'var(--danger)' }}>✕</button>
                </>
              )}
              <button style={s.closeBtn} onClick={onClose}>×</button>
            </div>
          </div>

          {playbook.description && (
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.65, margin: '12px 0 0' }}>
              {playbook.description}
            </p>
          )}
        </div>

        {/* Skills list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ ...s.sectionLabel, marginBottom: 4 }}>Skills in this playbook</div>
          {skills.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--dim)', textAlign: 'center', padding: '24px 0' }}>No skills yet</div>
          ) : skills.map((skill, i) => {
            const sc = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];
            const typeInfo = OUTPUT_TYPES.find(t => t.id === skill.type) || { icon: '📄', label: skill.type };
            return (
              <button
                key={skill.id || i}
                onClick={() => onSkillClick(skill)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  background: 'var(--surface2)', border: '0.5px solid var(--border)',
                  borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                  textAlign: 'left', width: '100%', fontFamily: 'var(--font)',
                  transition: 'border-color 0.12s, background 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = color + '60'; e.currentTarget.style.background = 'var(--surface3)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface2)'; }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                  background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
                }}>{sc.icon}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>{skill.title}</div>
                  {skill.description && (
                    <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {skill.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                    <span className={`tag tag-${skill.type}`} style={{ fontSize: 10 }}>{typeInfo.icon} {typeInfo.label}</span>
                    {skill.model && <span className="model-badge" style={{ fontSize: 10 }}>{skill.model}</span>}
                    {(skill.variables || []).length > 0 && (
                      <span style={{ fontSize: 10, color: 'var(--dim)' }}>{skill.variables.length} variable{skill.variables.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: 14, color: 'var(--dim)', flexShrink: 0, alignSelf: 'center' }}>›</span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '0.5px solid var(--border)', flexShrink: 0 }}>
          {isOwner ? (
            <div style={{ fontSize: 12, color: 'var(--dim)', textAlign: 'center' }}>You own this playbook</div>
          ) : (
            <button
              onClick={() => { onInstall(playbook); onClose(); }}
              disabled={installing}
              style={{
                width: '100%', padding: '11px', borderRadius: 'var(--radius-sm)',
                background: installing ? 'var(--surface2)' : `${color}20`,
                border: `0.5px solid ${color}60`,
                color: installing ? 'var(--dim)' : color,
                fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600,
                cursor: installing ? 'not-allowed' : 'pointer',
                transition: 'all 0.12s',
              }}>
              {installing ? 'Installing...' : `⬇ Install playbook — ${skills.length} skills`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// -- Playbook Card ─────────────────────────────────────────────────
function PlaybookCard({ playbook, isOwner, onView, onEdit, onDelete, onInstall, onPublish, installing, folders, userId, isListed }) {
  const catInfo = SKILL_CATEGORIES.find(c => c.id === playbook.category) || SKILL_CATEGORIES[0];
  const color = playbook.cover_color || '#7f77dd';
  const [copySlug, setCopySlug] = useState(false);

  const handleCopySlug = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(playbook.slug);
    setCopySlug(true);
    setTimeout(() => setCopySlug(false), 1800);
  };

  return (
    <div
      onClick={() => onView && onView(playbook)}
      style={{
        background: 'var(--surface)', border: '0.5px solid var(--border)',
        borderRadius: 'var(--radius)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transition: 'border-color 0.12s, transform 0.12s',
        cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color + '80'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Colour banner — always present, carries visibility label */}
      <div style={{
        height: 28, background: color,
        boxShadow: `0 2px 12px ${color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {playbook.is_public
            ? <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '1px 7px' }}>Public</span>
            : <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '1px 7px' }}>Private</span>
          }
          {isListed && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '1px 7px' }}>✦ Listed</span>
          )}
        </div>
        {playbook.forked_from && (
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>⬇ Installed</span>
        )}
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {/* Icon slot — thumbnail if uploaded, category icon otherwise */}
          <div style={{
            width: 44, height: 44, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
            background: color + '20', border: `0.5px solid ${color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            {playbook.cover_image_url
              ? <img src={playbook.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : catInfo.icon
            }
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {playbook.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 1 }}>
              {catInfo.label}
              {playbook.profiles?.username && <span> · @{playbook.profiles.username}</span>}
            </div>
          </div>

          {isOwner && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {userId && <FolderPicker itemType="playbook" itemId={playbook.id} userId={userId} folders={folders} />}
              <button onClick={e => { e.stopPropagation(); onEdit(playbook); }} style={s.iconActionBtn} title="Edit">✎</button>
              <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete this playbook?')) onDelete(playbook.id); }}
                style={{ ...s.iconActionBtn, color: 'var(--danger)' }}>✕</button>
            </div>
          )}
        </div>

        {playbook.description && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>{playbook.description}</div>
        )}

        {/* Skills preview */}
        {(playbook.skills || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(playbook.skills || []).slice(0, 4).map((skill, i) => {
              const sc = SKILL_CATEGORIES.find(c => c.id === skill.category) || SKILL_CATEGORIES[0];
              return (
                <div key={skill.id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  fontSize: 12, color: 'var(--muted)', padding: '4px 8px',
                  background: 'var(--surface2)', borderRadius: 5,
                }}>
                  <span style={{ fontSize: 11 }}>{sc.icon}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {skill.title}
                  </span>
                  {(skill.variables || []).length > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--dim)', flexShrink: 0 }}>
                      {skill.variables.length} var{skill.variables.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              );
            })}
            {(playbook.skills || []).length > 4 && (
              <div style={{ fontSize: 11, color: 'var(--dim)', padding: '2px 8px' }}>
                + {playbook.skills.length - 4} more skill{playbook.skills.length - 4 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Footer meta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
          <span style={{ fontSize: 11, color: 'var(--dim)' }}>
            {(playbook.skills || []).length} skill{(playbook.skills || []).length !== 1 ? 's' : ''}
          </span>
          {playbook.install_count > 0 && (
            <span style={{ fontSize: 11, color: 'var(--dim)' }}>· {playbook.install_count} installs</span>
          )}

          {playbook.slug && (
            <button onClick={handleCopySlug} title="Copy CLI slug" style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, color: copySlug ? 'var(--accent)' : 'var(--dim)',
              fontFamily: 'var(--mono)', display: 'flex', alignItems: 'center', gap: 3, padding: 0,
            }}>
              <span style={{ fontSize: 9 }}>⌘</span>
              {copySlug ? 'Copied!' : playbook.slug}
            </button>
          )}
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', gap: 7 }}>
          {!isOwner && (
            <button
              onClick={e => { e.stopPropagation(); onInstall(playbook); }}
              disabled={installing}
              style={{
                flex: 1, padding: '9px', borderRadius: 'var(--radius-sm)',
                background: installing ? 'var(--surface2)' : `${color}20`,
                border: `0.5px solid ${color}60`,
                color: installing ? 'var(--dim)' : color,
                fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
                cursor: installing ? 'not-allowed' : 'pointer',
                transition: 'all 0.12s',
              }}>
              {installing ? 'Installing...' : `⬇ Install (${(playbook.skills || []).length} skills)`}
            </button>
          )}
          {isOwner && onPublish && playbook.is_public && (
            isListed
              ? <button onClick={e => { e.stopPropagation(); onPublish(playbook); }}
                  style={{
                    flex: 1, padding: '9px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(127,119,221,0.12)', border: '0.5px solid rgba(127,119,221,0.35)',
                    color: '#7f77dd', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}>
                  ✦ On Marketplace
                </button>
              : <button onClick={e => { e.stopPropagation(); onPublish(playbook); }}
                  style={{
                    flex: 1, padding: '9px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)',
                    color: '#1d9e75', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}>
                  Publish to Marketplace →
                </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
// ── Publish to Marketplace modal ─────────────────────────────────────────────
const COVER_COLORS = ['#7c6ffb','#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#06b6d4','#f97316','#1d9e75'];

function PublishModal({ item, itemType, onClose, onPublished, userId, isListed }) {
  // If already listed, load the existing listing to edit it
  const [existingListing, setExistingListing] = useState(null);
  const [, setLoadingExisting]  = useState(isListed);
  useEffect(() => {
    if (!isListed) return;
    import('../lib/supabase').then(async ({ supabase }) => {
      const { data } = await supabase
        .from('marketplace_listings')
        .select('*')
        .eq('item_id', item.id)
        .eq('status', 'published')
        .single();
      setExistingListing(data || null);
      setLoadingExisting(false);
    });
  }, [item.id, isListed]);

  const [desc,      setDesc]      = useState(item.description || '');
  // Update desc when existing listing loads
  useEffect(() => { if (existingListing?.description) setDesc(existingListing.description); }, [existingListing]);
  const [tags,      setTags]      = useState((item.tags || []).join(', '));

  // Sync from existing listing when it loads
  useEffect(() => {
    if (!existingListing) return;
    if (existingListing.description) setDesc(existingListing.description);
    if (existingListing.tags?.length) setTags(existingListing.tags.join(', '));
    if (existingListing.cover_color) setColor(existingListing.cover_color);
    if (existingListing.cover_image_url) setImageUrl(existingListing.cover_image_url);
  }, [existingListing]);
  const [color,     setColor]     = useState(item.cover_color || '#7c6ffb');
  const [imageUrl,  setImageUrl]  = useState(item.cover_image_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [done,      setDone]      = useState(false);
  const isPlaybook = itemType === 'playbook';

  const handlePublish = async () => {
    if (!desc.trim()) { alert('Please add a description so users know what this does.'); return; }
    setSaving(true);
    try {
      await publishListing({
        userId,
        itemType,
        itemId:         item.id,
        title:          item.title,
        description:    desc.trim(),
        category:       item.category,
        coverColor:     color,
        coverImageUrl:  imageUrl || null,
        tags:           tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setDone(true);
      onPublished?.();
      setTimeout(onClose, 1800);
    } catch (e) { alert('Publish failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10200, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 500, border: '0.5px solid var(--border2)', maxHeight: '90vh', overflow: 'auto' }}>

        {/* Colour strip preview */}
        <div style={{ height: 5, background: `linear-gradient(90deg, ${color}, ${color}66)`, borderRadius: '16px 16px 0 0' }} />

        <div style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                {isListed ? '✦ Update Listing' : '🚀 Publish to Marketplace'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {isPlaybook ? '📦' : '⚡'} {item.title}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', fontSize: 20 }}>×</button>
          </div>

          {/* What gets shown to buyers */}
          <div style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8, border: '0.5px solid var(--border)', marginBottom: 16, fontSize: 12, color: 'var(--dim)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--muted)' }}>What buyers see:</strong> your title, description, tags, category
            {!isPlaybook && ', prompt preview, variables, and model'}
            {isPlaybook && ', and the list of skills included'}
          </div>

          {/* Description — required */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)', marginBottom: 5 }}>
              Description <span style={{ color: '#e24b4a' }}>*</span>
            </div>
            <textarea
              value={desc} onChange={e => setDesc(e.target.value)}
              placeholder={isPlaybook
                ? "What problem does this playbook solve? Who is it for? What will they be able to do after installing it?"
                : "What does this skill do? What inputs does it need? What output does it produce?"
              }
              style={{ width: '100%', background: 'var(--bg)', border: `0.5px solid ${!desc.trim() ? 'rgba(226,75,74,0.5)' : 'var(--border2)'}`, borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, minHeight: 90, resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
            />
          </div>

          {/* Tags */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)', marginBottom: 5 }}>
              Tags <span style={{ color: 'var(--dim)', fontWeight: 400, textTransform: 'none', fontSize: 11 }}>(comma-separated, helps with search)</span>
            </div>
            <input
              value={tags} onChange={e => setTags(e.target.value)}
              placeholder="e.g. writing, marketing, email, social media"
              style={{ width: '100%', background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Cover colour */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)', marginBottom: 8 }}>Accent colour</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {COVER_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)} style={{
                  width: 28, height: 28, borderRadius: '50%', background: c, border: 'none',
                  cursor: 'pointer', flexShrink: 0,
                  outline: color === c ? `3px solid ${c}` : 'none',
                  outlineOffset: 2,
                  transform: color === c ? 'scale(1.15)' : 'scale(1)',
                  transition: 'transform 0.1s',
                }} />
              ))}
            </div>
          </div>

          {/* Cover image */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--dim)', marginBottom: 8 }}>
              Cover image <span style={{ color: 'var(--dim)', fontWeight: 400, textTransform: 'none', fontSize: 11 }}>(optional — shows on marketplace card)</span>
            </div>
            {imageUrl ? (
              <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                <img src={imageUrl} alt="Cover" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                <button onClick={() => setImageUrl('')} style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
            ) : (
              <label style={{ display: 'block', cursor: 'pointer' }}>
                <div style={{ border: '1.5px dashed var(--border2)', borderRadius: 8, padding: '16px', textAlign: 'center', background: 'var(--bg)', transition: 'border-color 0.15s' }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>🖼</div>
                  <div style={{ fontSize: 12, color: 'var(--dim)' }}>
                    {uploading ? 'Uploading…' : 'Click to upload image'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 3 }}>PNG, JPG up to 5MB</div>
                </div>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10MB'); return; }
                  setUploading(true);
                  try {
                    // Auto-crop to square before uploading
                    const cropped = await cropSquare(file, 1080);
                    const { supabase } = await import('../lib/supabase');
                    const path = `marketplace-covers/${userId}-${Date.now()}.jpg`;
                    const { error } = await supabase.storage.from('entry-files').upload(path, cropped, { upsert: true, contentType: 'image/jpeg' });
                    if (error) throw error;
                    const { data: urlData } = supabase.storage.from('entry-files').getPublicUrl(path);
                    setImageUrl(urlData.publicUrl);
                  } catch(err) { alert('Upload failed: ' + err.message); }
                  setUploading(false);
                }} />
              </label>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '11px', background: 'none', border: '0.5px solid var(--border2)', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handlePublish} disabled={saving || done} style={{ flex: 2, padding: '11px', background: done ? 'rgba(29,158,117,0.15)' : 'var(--accent)', color: done ? '#1d9e75' : '#fff', border: done ? '0.5px solid rgba(29,158,117,0.4)' : 'none', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: saving ? 'wait' : 'pointer', transition: 'all 0.2s' }}>
              {done ? (isListed ? '✓ Updated!' : '✓ Published!') : saving ? 'Saving…' : isListed ? 'Update listing' : '🚀 Publish to Marketplace'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reusable image upload widget for skills ──────────────────────────────
function SkillImageUpload({ value, onChange, userId, placeholder, folder = 'skill-images' }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10MB'); return; }
    setUploading(true);
    try {
      // Auto-crop to square (1080×1080 max) before uploading
      const cropped = await cropSquare(file, 1080);
      const { supabase } = await import('../lib/supabase');
      const path = `${folder}/${userId}-${Date.now()}.jpg`;
      const { error } = await supabase.storage.from('entry-files').upload(path, cropped, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data } = supabase.storage.from('entry-files').getPublicUrl(path);
      onChange(data.publicUrl);
    } catch (err) { alert('Upload failed: ' + err.message); }
    setUploading(false);
  };

  return (
    <div>
      {value ? (
        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '0.5px solid var(--border2)' }}>
          <img src={value} alt="preview" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
          <button onClick={() => onChange('')} style={{
            position: 'absolute', top: 6, right: 6,
            background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%',
            width: 24, height: 24, cursor: 'pointer', color: '#fff', fontSize: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
      ) : (
        <label style={{ cursor: uploading ? 'wait' : 'pointer', display: 'block' }}>
          <div style={{
            border: '1.5px dashed var(--border2)', borderRadius: 8,
            padding: '14px', textAlign: 'center',
            background: 'var(--bg)', transition: 'border-color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>🖼</div>
            <div style={{ fontSize: 12, color: 'var(--dim)' }}>
              {uploading ? 'Uploading…' : placeholder || 'Click to upload image'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--dim)', marginTop: 2 }}>PNG, JPG, GIF up to 8MB</div>
          </div>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

export default function SkillsPage() {
  const { user, isPro, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initTab = searchParams.get('tab');
  // Load personal folders so FolderPicker and folder-filtering work
  const [folders, setFolders] = useState([]);
  useEffect(() => {
    if (!user) return;
    import('../lib/supabase').then(({ fetchFolders }) =>
      fetchFolders(user.id).then(setFolders).catch(console.error)
    );
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track which item IDs are already listed on the marketplace
  const [listedIds, setListedIds] = useState(new Set());
  const refreshListedIds = useCallback(() => {
    if (!user) return;
    import('../lib/supabase').then(async ({ supabase }) => {
      const { data } = await supabase
        .from('marketplace_listings')
        .select('item_id')
        .eq('user_id', user.id)
        .eq('status', 'published');
      setListedIds(new Set((data || []).map(r => r.item_id)));
    }).catch(console.error);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refreshListedIds(); }, [refreshListedIds]);

  // Unified folder view state
  const [selectedFolderEntry, setSelectedFolderEntry] = useState(null);
  const [folderItems, setFolderItems]         = useState({ entries: [], skills: [], playbooks: [] });
  const [folderLoading, setFolderLoading]      = useState(false);
  const [folderTypeFilter, setFolderTypeFilter] = useState('all'); // all | entry | skill | playbook
  const canCreateUnlimited = isPro || isAdmin;
  const parseTab = (t) =>
    t === 'browse' ? 'browse' :
    t === 'playbooks'  ? 'playbooks'  :
    // browse tabs now live in /marketplace
    t === 'forked' ? 'forked' : 'mine';

  const [tab, setTab] = useState(() => parseTab(initTab) || 'mine');
  const activeFolderParam = searchParams.get('folder') || null;

  // Sync tab when URL search params change (sidebar navigation)
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    setTab(parseTab(tabParam));
    setSearch('');
    setCategoryFilter('all');
  }, [tabParam]); // eslint-disable-line react-hooks/exhaustive-deps
  const [skills, setSkills]         = useState([]);
  const [playbooks, setPlaybooks]           = useState([]);
  // Derived counts — must be AFTER skills and playbooks useState declarations
  const createdSkillCount   = skills.filter(sk => sk.user_id === user?.id && !sk.forked_from).length;
  const canCreateSkill      = canCreateUnlimited || createdSkillCount < FREE_SKILL_CREATE_LIMIT; // eslint-disable-line no-unused-vars
  const canCreatePlaybook   = canCreateUnlimited || (playbooks.filter(p => !p.forked_from && p.user_id === user?.id).length < FREE_PLAYBOOK_CREATE_LIMIT); // eslint-disable-line no-unused-vars
  const [mySkills, setMySkills]     = useState([]); // always loaded for playbook form
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm]     = useState(false);
  const [showPlaybookForm, setShowPlaybookForm] = useState(false);
  const [editSkill, setEditSkill]   = useState(null);
  const [editPlaybook, setEditPlaybook]     = useState(null);
  const [useSkill, setUseSkill]       = useState(null);
  const [detailSkill, setDetailSkill] = useState(null);   // SkillDetailModal
  const [detailPlaybook, setDetailPlaybook] = useState(null); // PlaybookDetailModal
  const [forkToast, setForkToast]   = useState('');
  const [installing, setInstalling] = useState(null); // playbook id being installed
  const [publishItem, setPublishItem] = useState(null); // { item, itemType }

  // No redirect — free/logged-out users can browse public skills & playbooks

  // Always keep mySkills fresh for the playbook form
  useEffect(() => {
    if (!user) return;
    import('../lib/supabase').then(m => m.fetchMySkills(user.id))
      .then(d => setMySkills(d || [])).catch(console.error);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) { setLoading(false); return; }  // all signed-in users can load their skills
    setLoading(true);
    if (tab === 'mine') {
      if (activeFolderParam) {
        // Folder view — load only skills/playbooks in this folder
        import('../lib/supabase').then(async ({ fetchSkillsInFolder }) => {
          const { skills: folderSkills } = await fetchSkillsInFolder(activeFolderParam, user.id);
          setSkills(folderSkills || []);
        }).catch(console.error).finally(() => setLoading(false));
      } else {
        import('../lib/supabase').then(m => m.fetchMySkills(user.id))
          .then(d => setSkills(d || [])).catch(console.error).finally(() => setLoading(false));
      }
    } else if (tab === 'playbooks') {
      if (activeFolderParam) {
        import('../lib/supabase').then(async ({ fetchSkillsInFolder }) => {
          const { playbooks: folderPacks } = await fetchSkillsInFolder(activeFolderParam, user.id);
          setPlaybooks(folderPacks || []);
        }).catch(console.error).finally(() => setLoading(false));
      } else {
        import('../lib/supabase').then(m => m.fetchMyPlaybooks(user.id))
          .then(d => setPlaybooks(d || [])).catch(console.error).finally(() => setLoading(false));
      }
    } else {
      setLoading(false);
    }
  }, [tab, user, isPro, isAdmin, search, activeFolderParam]);

  // Load ALL items (entries + skills + playbooks) when folder is active
  useEffect(() => {
    if (!activeFolderParam || !user) { setFolderItems({ entries: [], skills: [], playbooks: [] }); return; }
    setFolderLoading(true);
    import('../lib/supabase').then(({ fetchFolderAllItems }) =>
      fetchFolderAllItems(activeFolderParam, user.id)
        .then(setFolderItems)
        .catch(console.error)
        .finally(() => setFolderLoading(false))
    );
  }, [activeFolderParam, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPackTab  = tab === 'playbooks';
  const isSkillTab = tab === 'mine';

  // Sub-filter state — must be declared before the useMemos that use them
  const [skillFilter,    setSkillFilter]    = useState('all');  // all | created | installed
  const [playbookFilter, setPlaybookFilter] = useState('all');  // all | created | installed

  const filteredSkills = useMemo(() => {
    let list = skills;
    // Sub-filter: created by me vs installed from marketplace
    if (skillFilter === 'created')   list = list.filter(sk => !sk.forked_from);
    if (skillFilter === 'installed') list = list.filter(sk =>  !!sk.forked_from);
    if (categoryFilter !== 'all') list = list.filter(sk => sk.category === categoryFilter);
    if (tab === 'mine' && search) {
      const q = search.toLowerCase();
      list = list.filter(sk =>
        sk.title?.toLowerCase().includes(q) ||
        sk.description?.toLowerCase().includes(q) ||
        (sk.tags || []).join(' ').toLowerCase().includes(q)
      );
    }
    return list;
  }, [skills, search, tab, categoryFilter, skillFilter]);

  const filteredPlaybooks = useMemo(() => {
    let list = playbooks;
    if (playbookFilter === 'created')   list = list.filter(p => !p.forked_from);
    if (playbookFilter === 'installed') list = list.filter(p =>  !!p.forked_from);
    if (categoryFilter !== 'all') list = list.filter(p => p.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.title?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
    }
    return list;
  }, [playbooks, search, categoryFilter, playbookFilter]);

  const handleSaveSkill = (saved) => {
    setSkills(prev => {
      const exists = prev.find(s => s.id === saved.id);
      return exists ? prev.map(s => s.id === saved.id ? saved : s) : [saved, ...prev];
    });
    setMySkills(prev => {
      const exists = prev.find(s => s.id === saved.id);
      return exists ? prev.map(s => s.id === saved.id ? saved : s) : [saved, ...prev];
    });
    if (!editSkill) analytics.skillCreated({ category: saved.category });
    setShowForm(false); setEditSkill(null);
  };

  const handleDeleteSkill = async (id) => {
    const { deleteSkill } = await import('../lib/supabase');
    await deleteSkill(id, user.id);
    setSkills(prev => prev.filter(s => s.id !== id));
    setMySkills(prev => prev.filter(s => s.id !== id));
  };

  const handleFork = async (skill) => {
    const { forkSkill } = await import('../lib/supabase');
    const forked = await forkSkill(skill, user.id);
    if (tab === 'mine') setSkills(prev => [forked, ...prev]);
    setMySkills(prev => [forked, ...prev]);
    setForkToast('🍴 Skill forked to My skills');
    setTimeout(() => setForkToast(''), 2200);
  };

  const handleSavePlaybook = (saved) => {
    setPlaybooks(prev => {
      const exists = prev.find(p => p.id === saved.id);
      return exists ? prev.map(p => p.id === saved.id ? saved : p) : [saved, ...prev];
    });
    setShowPlaybookForm(false); setEditPlaybook(null);
  };

  const handleDeletePlaybook = async (id) => {
    const { deletePlaybook } = await import('../lib/supabase');
    await deletePlaybook(id, user.id);
    setPlaybooks(prev => prev.filter(p => p.id !== id));
  };

  const handleInstallPlaybook = async (playbook) => {
    setInstalling(playbook.id);
    try {
      const { installPlaybook, fetchMyPlaybooks } = await import('../lib/supabase');
      const result = await installPlaybook(playbook, user.id);
      const { skills: installedSkills } = result;

      // Add forked skills to My Skills
      if (installedSkills?.length) {
        setMySkills(prev => [...installedSkills, ...prev]);
      }

      // Re-fetch My Playbooks fresh from DB so the installed copy appears
      // regardless of which tab we're currently on
      const freshPlaybooks = await fetchMyPlaybooks(user.id);
      setPlaybooks(freshPlaybooks || []);

      // Switch to My Playbooks tab so the user sees the result immediately
      setTab('playbooks');
      setSearch('');
      setCategoryFilter('all');

      const skillCount = installedSkills?.length || 0;
      analytics.playbookInstalled({ slug: playbook.slug, skillCount });
      setForkToast(
        skillCount > 0
          ? `⬇ Installed — ${skillCount} skill${skillCount !== 1 ? 's' : ''} added to your library`
          : `⬇ Playbook installed`
      );
      setTimeout(() => setForkToast(''), 4000);
    } catch (err) {
      console.error('installPlaybook error:', err);
      alert('Install failed: ' + err.message);
    }
    setInstalling(null);
  };

  // Tabs
  const TABS = [
    { id: 'mine',      label: 'My Skills',    icon: '⚡', group: 'library', desc: 'Your skill library' },
    { id: 'playbooks', label: 'My Playbooks',  icon: '📦', group: 'library', desc: 'Your playbook library' },
  ];



  // All tabs are library tabs — gated behind Pro
  const showUpgradeWall = !user;  // only block signed-out users

  // ── Shared modal renderer — used by both folder view and normal view ────
  const renderModals = () => (
    <>
      {showForm && (
        <SkillForm skill={editSkill} onSave={handleSaveSkill}
          onClose={() => { setShowForm(false); setEditSkill(null); }} />
      )}
      {showPlaybookForm && (
        <PlaybookForm playbook={editPlaybook} mySkills={mySkills} onSave={handleSavePlaybook}
          onClose={() => { setShowPlaybookForm(false); setEditPlaybook(null); }} />
      )}
      {useSkill && <UseSkillModal skill={useSkill} onClose={() => setUseSkill(null)} />}
      {detailPlaybook && (
        <PlaybookDetailModal
          playbook={detailPlaybook}
          isOwner={detailPlaybook.user_id === user?.id || (isAdmin && detailPlaybook.user_id !== TEMPLATE_USER_ID)}
          installing={installing === detailPlaybook.id}
          onClose={() => setDetailPlaybook(null)}
          onInstall={handleInstallPlaybook}
          onEdit={p => { setDetailPlaybook(null); setEditPlaybook(p); setShowPlaybookForm(true); }}
          onDelete={id => { handleDeletePlaybook(id); setDetailPlaybook(null); }}
          onSkillClick={sk => setDetailSkill(sk)}
        />
      )}
      {detailSkill && (
        <SkillDetailModal
          skill={detailSkill}
          isOwner={detailSkill.user_id === user?.id || (isAdmin && detailSkill.user_id !== TEMPLATE_USER_ID)}
          onClose={() => setDetailSkill(null)}
          onUse={sk => { setDetailSkill(null); setUseSkill(sk); }}
          onEdit={sk => { setDetailSkill(null); setEditSkill(sk); setShowForm(true); }}
          onFork={async (sk) => {
            const { forkSkill } = await import('../lib/supabase');
            await forkSkill(sk.id, user.id);
            setDetailSkill(null);
            setForkToast(`🍴 Forked "${sk.title}" into your library`);
            setTimeout(() => setForkToast(''), 3000);
          }}
        />
      )}
      <div className={`toast ${forkToast ? 'show' : ''}`}>{forkToast}</div>
      {publishItem && (
        <PublishModal
          item={publishItem.item}
          itemType={publishItem.itemType}
          userId={user.id}
          isListed={listedIds.has(publishItem.item?.id)}
          onClose={() => setPublishItem(null)}
          onPublished={() => {
            refreshListedIds();
            setForkToast(listedIds.has(publishItem.item?.id) ? '✦ Listing updated!' : '🚀 Published to Marketplace!');
            setTimeout(() => setForkToast(''), 3000);
          }}
        />
      )}
    </>
  );

  // ── Folder view — unified list when a folder is active ──────────────────
  if (activeFolderParam) {
    const folder = (folders || []).find(f => f.id === activeFolderParam);
    const allFolderItems = [
      ...folderItems.entries,
      ...folderItems.skills,
      ...folderItems.playbooks,
    ];
    const filteredFolderItems = folderTypeFilter === 'all'
      ? allFolderItems
      : allFolderItems.filter(i => i.item_kind === folderTypeFilter);

    return (
      <div style={s.page}>
        {/* Folder header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {folder && <span style={{ width: 12, height: 12, borderRadius: '50%', background: folder.color, flexShrink: 0, boxShadow: `0 0 8px ${folder.color}` }} />}
            <h1 style={{ fontSize: 'clamp(18px,5vw,22px)', fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '-0.02em' }}>
              {folder?.name || 'Folder'}
            </h1>
            <button onClick={() => { setSearchParams(p => { const n = new URLSearchParams(p); n.delete('folder'); return n; }); setFolderTypeFilter('all'); }}
              style={{ background: 'none', border: '0.5px solid var(--border2)', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 11, color: 'var(--dim)' }}>
              ← All items
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>
            {folderLoading ? 'Loading…' : `${allFolderItems.length} item${allFolderItems.length !== 1 ? 's' : ''}`}
          </div>
        </div>

        {/* Type filter pills */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { id: 'all',      label: 'All',       icon: '⊟' },
            { id: 'entry',    label: 'Prompts',    icon: '📄', count: folderItems.entries.length },
            { id: 'skill',    label: 'Skills',     icon: '⚡', count: folderItems.skills.length },
            { id: 'playbook', label: 'Playbooks',  icon: '📦', count: folderItems.playbooks.length },
          ].filter(f => f.id === 'all' || f.count > 0).map(f => (
            <button key={f.id} onClick={() => setFolderTypeFilter(f.id)} style={{
              padding: '5px 13px', borderRadius: 20, border: '0.5px solid var(--border2)',
              fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer',
              background: folderTypeFilter === f.id ? 'var(--accent-dim)' : 'none',
              color:      folderTypeFilter === f.id ? 'var(--accent)'     : 'var(--muted)',
              fontWeight: folderTypeFilter === f.id ? 600 : 400,
              borderColor: folderTypeFilter === f.id ? 'var(--accent-border)' : 'var(--border2)',
              transition: 'all 0.12s',
            }}>
              {f.icon} {f.label}{f.count !== undefined ? ` (${f.count})` : ''}
            </button>
          ))}
        </div>

        {/* Unified item list */}
        {folderLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)', fontSize: 13 }}>Loading…</div>
        ) : filteredFolderItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)' }}>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.4 }}>📁</div>
            <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 6 }}>This folder is empty</div>
            <div style={{ fontSize: 13, color: 'var(--dim)' }}>
              Add prompts from your library or use the 📁 button on any skill or playbook.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredFolderItems.map(item => {
              if (item.item_kind === 'entry') return (
                <div key={item.id}
                  onClick={() => setSelectedFolderEntry(item)}
                  style={{
                    background: 'var(--surface)', border: '0.5px solid var(--border)',
                    borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    transition: 'border-color 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--dim)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span>{item.model || 'Prompt'}</span>
                      {item.type && <span className={`tag tag-${item.type}`} style={{ fontSize: 9 }}>{item.type}</span>}
                      {(item.tags || []).slice(0, 2).map(t => <span key={t} className="tag tag-custom" style={{ fontSize: 9 }}>{t}</span>)}
                    </div>
                  </div>
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, flexShrink: 0,
                    background: 'rgba(95,94,90,0.1)', color: 'var(--dim)', border: '0.5px solid var(--border)' }}>Prompt</span>
                </div>
              );
              if (item.item_kind === 'skill') return (
                <SkillCard key={item.id} skill={item}
                  isOwner={item.user_id === user?.id}
                  currentUserId={user?.id}
                  folders={folders}
                  userId={user?.id}
                  onView={sk => setDetailSkill(sk)}
                  onUse={sk => setUseSkill(sk)}
                  onEdit={sk => { setEditSkill(sk); setShowForm(true); }}
                  onPublish={sk => setPublishItem({ item: sk, itemType: 'skill' })}
                  onDelete={handleDeleteSkill}
                  onFork={handleFork}
                />
              );
              if (item.item_kind === 'playbook') return (
                <PlaybookCard key={item.id} playbook={item}
                  isOwner={item.user_id === user?.id}
                  folders={folders}
                  userId={user?.id}
                  onView={p => setDetailPlaybook(p)}
                  onEdit={p => { setEditPlaybook(p); setShowPlaybookForm(true); }}
                  onDelete={handleDeletePlaybook}
                  onPublish={p => setPublishItem({ item: p, itemType: 'playbook' })}
                  onInstall={handleInstallPlaybook}
                  installing={installing === item.id}
                />
              );
              return null;
            })}
          </div>
        )}

        {/* Entry detail panel */}
        {selectedFolderEntry && (
          <DetailPanel
            entry={selectedFolderEntry}
            onClose={() => setSelectedFolderEntry(null)}
            onDeleted={id => {
              setSelectedFolderEntry(null);
              setFolderItems(prev => ({ ...prev, entries: prev.entries.filter(e => e.id !== id) }));
            }}
            onUpdated={updated => {
              setFolderItems(prev => ({
                ...prev,
                entries: prev.entries.map(e => e.id === updated.id ? { ...updated, item_kind: 'entry' } : e),
              }));
            }}
            folders={folders}
          />
        )}
        {/* Other modals (skill/playbook forms, etc.) */}
        {renderModals()}
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* ── Header — title + action button on one line ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, gap: 10,
      }}>
        <div>
          <h1 style={{ fontSize: 'clamp(18px,5vw,24px)', fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {isPackTab ? 'My Playbooks' : 'My Skills'}
          </h1>
          {activeFolderParam && (() => {
            const f = (folders || []).find(x => x.id === activeFolderParam);
            return f ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{f.name}</span>
                <button onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.delete('folder'); return n; })}
                  style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              </div>
            ) : null;
          })()}
          {!canCreateUnlimited && user && isSkillTab && (() => {
            // Only count skills the user CREATED (forked_from = null), not installed ones
            const myCount = skills.filter(sk => sk.user_id === user?.id && !sk.forked_from).length;
            const remaining = Math.max(FREE_SKILL_CREATE_LIMIT - myCount, 0);
            if (myCount === 0) return null;
            return (
              <div style={{ fontSize: 11, color: remaining === 0 ? '#e24b4a' : 'var(--dim)', marginTop: 2 }}>
                {remaining === 0
                  ? <><a href="/premium" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Upgrade</a> for unlimited</>
                  : `${remaining} of ${FREE_SKILL_CREATE_LIMIT} free skill${remaining !== 1 ? 's' : ''} remaining`
                }
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {isSkillTab && (
            <button onClick={() => {
              if (!canCreateUnlimited && skills.filter(sk => sk.user_id === user?.id && !sk.forked_from).length >= FREE_SKILL_CREATE_LIMIT) {
                alert(`Free accounts can create up to ${FREE_SKILL_CREATE_LIMIT} skills. Upgrade to Pro for unlimited.`);
                return;
              }
              setEditSkill(null); setShowForm(true);
            }} style={s.newBtn}>+ New skill</button>
          )}
          {isPackTab && (
            <button onClick={() => {
              if (!canCreateUnlimited && playbooks.filter(p => !p.forked_from && p.user_id === user?.id).length >= FREE_PLAYBOOK_CREATE_LIMIT) {
                alert(`Free accounts can create up to ${FREE_PLAYBOOK_CREATE_LIMIT} playbooks. Upgrade to Pro for unlimited.`);
                return;
              }
              setEditPlaybook(null); setShowPlaybookForm(true);
            }} style={s.newBtn}>
              + New playbook
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs: My Skills | My Playbooks ── */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)', marginBottom: 14 }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id}
              onClick={() => { setTab(t.id); setSearch(''); setCategoryFilter('all'); setSkillFilter('all'); setPlaybookFilter('all'); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '8px 14px',
                border: 'none',
                borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                borderRadius: 0, background: 'none',
                fontFamily: 'var(--font)', fontSize: 13,
                fontWeight: active ? 600 : 400,
                color: active ? 'var(--accent)' : 'var(--muted)',
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.12s, border-color 0.12s',
                marginBottom: -1,
                WebkitTapHighlightColor: 'transparent',
              }}>
              <span>{t.icon}</span>{t.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <a href="/marketplace" style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '8px 10px', fontSize: 12, color: 'var(--dim)',
          textDecoration: 'none', alignSelf: 'center', marginBottom: 1,
          WebkitTapHighlightColor: 'transparent',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
          🛒 <span style={{ display: 'none' }}>Marketplace</span>→
        </a>
      </div>

      {/* Search + category filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, position: 'relative', minWidth: 160 }}>
          <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--dim)', fontSize: 14, pointerEvents: 'none' }}>⌕</span>
          <input style={{ ...s.searchInput, paddingLeft: 30 }}
            placeholder={isPackTab ? 'Search playbooks...' : 'Search skills...'}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Category pills — scroll left/right */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'nowrap', overflowX: 'auto', padding: '6px 0 10px 0', marginBottom: 12, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none' }}>
        <div onClick={() => setCategoryFilter('all')} style={{
          ...s.catPill,
          background: categoryFilter === 'all' ? 'var(--accent-dim)' : 'var(--surface)',
          borderColor: categoryFilter === 'all' ? 'var(--accent-border)' : 'var(--border2)',
          color: categoryFilter === 'all' ? 'var(--accent)' : 'var(--muted)',
        }}>All</div>
        {SKILL_CATEGORIES.map(c => (
          <div key={c.id} onClick={() => setCategoryFilter(c.id)} style={{
            ...s.catPill,
            background: categoryFilter === c.id ? 'var(--accent-dim)' : 'var(--surface)',
            borderColor: categoryFilter === c.id ? 'var(--accent-border)' : 'var(--border2)',
            color: categoryFilter === c.id ? 'var(--accent)' : 'var(--muted)',
          }}>{c.icon} {c.label}</div>
        ))}
      </div>

      {/* Sub-filter pills — Created / Installed within Mine and Playbooks tabs */}
      {!showUpgradeWall && isSkillTab && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { id: 'all',       label: 'All' },
            { id: 'created',   label: '✏️ Created by me' },
            { id: 'installed', label: '⬇ Installed' },
          ].map(f => (
            <button key={f.id} onClick={() => setSkillFilter(f.id)} style={{
              padding: '5px 13px', borderRadius: 20, border: '0.5px solid var(--border2)',
              fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer',
              background: skillFilter === f.id ? 'var(--accent-dim)' : 'none',
              color:      skillFilter === f.id ? 'var(--accent)'     : 'var(--muted)',
              fontWeight: skillFilter === f.id ? 600 : 400,
              borderColor: skillFilter === f.id ? 'var(--accent-border)' : 'var(--border2)',
              transition: 'all 0.12s',
            }}>{f.label}</button>
          ))}
          {skillFilter !== 'all' && (
            <span style={{ fontSize: 11, color: 'var(--dim)', alignSelf: 'center', marginLeft: 4 }}>
              {filteredSkills.length} skill{filteredSkills.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      {!showUpgradeWall && isPackTab && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { id: 'all',       label: 'All' },
            { id: 'created',   label: '✏️ Created by me' },
            { id: 'installed', label: '⬇ Installed' },
          ].map(f => (
            <button key={f.id} onClick={() => setPlaybookFilter(f.id)} style={{
              padding: '5px 13px', borderRadius: 20, border: '0.5px solid var(--border2)',
              fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer',
              background: playbookFilter === f.id ? 'var(--accent-dim)' : 'none',
              color:      playbookFilter === f.id ? 'var(--accent)'     : 'var(--muted)',
              fontWeight: playbookFilter === f.id ? 600 : 400,
              borderColor: playbookFilter === f.id ? 'var(--accent-border)' : 'var(--border2)',
              transition: 'all 0.12s',
            }}>{f.label}</button>
          ))}
          {playbookFilter !== 'all' && (
            <span style={{ fontSize: 11, color: 'var(--dim)', alignSelf: 'center', marginLeft: 4 }}>
              {filteredPlaybooks.length} playbook{filteredPlaybooks.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Content — show upgrade wall for gated tabs, otherwise normal content */}
      {showUpgradeWall ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '52px 24px', textAlign: 'center', gap: 12,
        }}>
          <div style={{ fontSize: 44, marginBottom: 4 }}>⚡</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
            Sign in to access your library
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)', maxWidth: 320, lineHeight: 1.6 }}>
            Create a free account to install skills, build playbooks, and save your favourite AI prompts.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="/auth" style={{ ...s.newBtn, textDecoration: 'none', padding: '10px 24px', fontSize: 14 }}>
                Sign in free →
              </a>
          </div>
          {/* Preview — blur a few skill-like boxes to show what they're missing */}
          <div style={{ marginTop: 24, width: '100%', maxWidth: 480, position: 'relative' }}>
            {[
              { title: 'LinkedIn Post Writer', cat: 'Marketing' },
              { title: 'Code Review Assistant', cat: 'Coding' },
              { title: 'Blog Outline Generator', cat: 'Writing' },
            ].map((p, i) => (
              <div key={i} style={{
                background: 'var(--surface)', border: '0.5px solid var(--border)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 12,
                filter: 'blur(2px)', opacity: 0.5, userSelect: 'none',
                pointerEvents: 'none',
              }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--surface2)', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--accent)' }}>{p.cat}</div>
                </div>
              </div>
            ))}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to bottom, transparent 0%, var(--bg) 80%)',
            }} />
          </div>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)', fontSize: 13 }}>Loading...</div>
      ) : isSkillTab ? (
        filteredSkills.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)' }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>⚡</div>
            <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 6 }}>
              {tab === 'mine' ? 'No skills yet' : 'No public skills found'}
            </div>
            {tab === 'mine' && (
              <button onClick={() => setShowForm(true)} style={{ ...s.newBtn, marginTop: 12 }}>+ Create first skill</button>
            )}
          </div>
        ) : (
          <>
            {/* UGC prompt — highlight most-used private skill */}
            {tab === 'mine' && (() => {
              const bestPrivate = filteredSkills
                .filter(s => !s.is_public && (s.use_count || 0) >= 3 && s.user_id === user?.id)
                .sort((a, b) => (b.use_count || 0) - (a.use_count || 0))[0];
              if (!bestPrivate) return null;
              return (
                <div style={{ marginBottom: 14, padding: '12px 16px', background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: 2 }}>
                      ⚡ "{bestPrivate.title}" has been used {bestPrivate.use_count} times
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Share it with the community — publish it to the Marketplace for others to benefit from.
                    </div>
                  </div>
                  <button onClick={() => setPublishItem({ item: bestPrivate, itemType: 'skill' })} style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontFamily: 'var(--font)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                    🚀 Publish it
                  </button>
                </div>
              );
            })()}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {filteredSkills.map(skill => (
              <SkillCard
                key={skill.id} skill={skill}
                isOwner={skill.user_id === user?.id || (isAdmin && skill.user_id !== TEMPLATE_USER_ID)}
                currentUserId={user?.id}
                folders={folders}
                userId={user?.id}
                isListed={listedIds.has(skill.id)}
                onView={sk => setDetailSkill(sk)}
                onUse={sk => setUseSkill(sk)}
                onEdit={sk => { setEditSkill(sk); setShowForm(true); }}
                onPublish={sk => { setPublishItem({ item: sk, itemType: 'skill' }); }}
                onDelete={handleDeleteSkill}
                onFork={handleFork}
              />
            ))}
          </div>
          </>
        )
      ) : (
        // Playbooks view
        filteredPlaybooks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--dim)' }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>📦</div>
            <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 6 }}>
              {tab === 'playbooks' ? 'No playbooks yet' : 'No public packs found'}
            </div>
            {tab === 'playbooks' && (
              <button onClick={() => setShowPlaybookForm(true)} style={{ ...s.newBtn, marginTop: 12 }}>
                + Create first playbook
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {filteredPlaybooks.map(playbook => (
              <PlaybookCard
                key={playbook.id} playbook={playbook}
                isOwner={playbook.user_id === user?.id || (isAdmin && playbook.user_id !== TEMPLATE_USER_ID)}
                folders={folders}
                userId={user?.id}
                isListed={listedIds.has(playbook.id)}
                onView={p => setDetailPlaybook(p)}
                onEdit={p => { setEditPlaybook(p); setShowPlaybookForm(true); }}
                onDelete={handleDeletePlaybook}
                onPublish={p => setPublishItem({ item: p, itemType: 'playbook' })}
                onInstall={handleInstallPlaybook}
                installing={installing === playbook.id}
              />
            ))}
          </div>
        )
      )}

      {renderModals()}
    </div>
  );
}

const s = {
  page:      { background: 'var(--bg)', padding: 'clamp(12px, 3vw, 24px)', fontFamily: 'var(--font)' },
  header:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12, flexWrap: 'wrap' },
  pageTitle: { fontSize: 22, fontWeight: 500, color: 'var(--text)', marginBottom: 4, letterSpacing: '-0.02em' },
  pageSub:   { fontSize: 13, color: 'var(--muted)' },
  backBtn:   { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '7px 14px', color: 'var(--muted)', fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer' },
  newBtn:    { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px 16px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  tabBtn:    { padding: '6px 14px', border: '0.5px solid', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font)', fontSize: 12.5, cursor: 'pointer' },
  catPill:   { flexShrink: 0, padding: '7px 15px', borderRadius: 20, fontSize: 12, border: '0.5px solid', cursor: 'pointer', transition: 'all 0.12s', whiteSpace: 'nowrap', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1, WebkitTapHighlightColor: 'transparent' },
  searchInput: { width: '100%', background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '7px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' },
  sectionLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 6, fontWeight: 500 },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  modal:     { background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-lg)', width: '100%' },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid var(--border)', gap: 12 },
  closeBtn:  { background: 'none', border: 'none', color: 'var(--dim)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 2, flexShrink: 0 },
  field:     { display: 'flex', flexDirection: 'column', gap: 5 },
  label:     { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', fontWeight: 500 },
  input:     { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%' },
  varInput:  { background: 'var(--bg)', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none', width: '100%', resize: 'none' },
  cancelBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)', padding: '9px 16px', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' },
  saveBtn:   { flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '9px', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  iconActionBtn: { background: 'none', border: '0.5px solid var(--border2)', borderRadius: 4, padding: '3px 8px', fontFamily: 'var(--font)', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' },
  useBtn:    { flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', padding: '8px', fontFamily: 'var(--font)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' },
};
